import React, { useEffect, useState, useRef } from 'react';
import { Vector as VectorSource } from 'ol/source';
import * as d3 from 'd3';
import axios from 'axios';
import { fromLonLat } from 'ol/proj';
import './DtmLayer.css';

const MIN_RADIUS = 3;
const MAX_RADIUS = 23;

const DtmLayer = ({ map, selectedViewCode, selectedPillarKey, selectedLocation, onLocationSelect }) => {
  const [data, setData] = useState([]);
  const containerRef = useRef(null);
  const circleGroupRef = useRef(null);
  const radiusScaleRef = useRef(null);
  const tooltipRef = useRef(null);

  // Format date for tooltip
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    }).replace(/ /g, ' '); // Replace potential non-breaking spaces with regular spaces
  };

  // Initialize container and SVG
  useEffect(() => {
    console.log('DTM Layer - Map initialization check:', {
      hasMap: !!map,
      hasMapTarget: map && !!map.getTargetElement(),
      mapType: map ? typeof map : 'undefined'
    });
    
    if (!map || !map.getTargetElement()) {
      console.warn('DTM Layer - Map or map target element is not available yet');
      return;
    }

    try {
      const mapTarget = map.getTargetElement();
      if (!mapTarget) {
        return;
      }

      // Remove any existing container
      const existingContainer = document.getElementById('dtm-layer-container');
      if (existingContainer) {
        existingContainer.remove();
      }

      const container = document.createElement('div');
      container.id = 'dtm-layer-container';
      container.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 2000; /* Set above Admin1Layer and symbol layers but below tooltips */
        overflow: visible;
      `;

      // Create SVG container
      const svg = d3.select(container)
        .append('svg')
        .attr('class', 'dtm-layer-svg')
        .style('position', 'absolute')
        .style('top', '0')
        .style('left', '0')
        .style('width', '100%')
        .style('height', '100%')
        .style('pointer-events', 'none')
        .style('z-index', '2000') /* Set above Admin1Layer and symbol layers but below tooltips */
        .style('overflow', 'visible');

      // Add shadow filter definition
      const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
      filter.setAttribute('id', 'dtm-circle-shadow');
      filter.innerHTML = `
        <feGaussianBlur in="SourceAlpha" stdDeviation="1"/>
        <feOffset dx="0" dy="0" result="offsetblur"/>
        <feFlood flood-color="rgba(0,109,135,0.3)"/>
        <feComposite in2="offsetblur" operator="in"/>
        <feMerge>
          <feMergeNode/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      `;
      defs.appendChild(filter);
      svg.node().appendChild(defs);

      // Add container to map first
      mapTarget.appendChild(container);
      containerRef.current = container;

      // Create circle group after SVG is in the DOM
      const circleGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      circleGroup.classList.add('circle-group');
      svg.node().appendChild(circleGroup);
      circleGroupRef.current = circleGroup;

      // Create tooltip
      const tooltip = document.createElement('div');
      tooltip.className = 'dtm-tooltip';
      tooltip.style.display = 'none';
      document.body.appendChild(tooltip);
      tooltipRef.current = tooltip;

      return () => {
        if (tooltipRef.current) {
          document.body.removeChild(tooltipRef.current);
          tooltipRef.current = null;
        }
        if (containerRef.current && mapTarget) {
          mapTarget.removeChild(containerRef.current);
          containerRef.current = null;
        }
        circleGroupRef.current = null;
      };
    } catch (error) {
      console.error('Error initializing DTM layer:', error);
    }
  }, [map]);

  // Update data
  useEffect(() => {
    // Check if we should display DTM data
    const shouldShowDtm = selectedPillarKey === 'internally_displaced_persons';
    
    console.log('DTM Layer - Pillar check:', { 
      hasMap: !!map, 
      hasMapTarget: map && !!map.getTargetElement(),
      selectedViewCode, 
      selectedPillarKey,
      isCorrectPillar: shouldShowDtm,
      defaultPillar: selectedPillarKey === undefined ? 'using default (internally_displaced_persons)' : 'using selected pillar'
    });
    
    if (!map) {
      console.warn('DTM Layer - Map is not available yet, waiting for map to initialize');
      return;
    }
    
    if (!map.getTargetElement()) {
      console.warn('DTM Layer - Map target element is not available yet');
      return;
    }
    
    if (!selectedViewCode || !shouldShowDtm) {
      console.log('DTM Layer - View or pillar conditions not met, clearing data');
      setData([]); // Clear data if pillar is not IDP
      return;
    }

    const fetchData = async () => {
      try {
        console.log('DTM Layer - Fetching data with params:', { 
          view: selectedViewCode,
          admin1Pcode: selectedLocation?.pcode 
        });
        
        console.log('DTM Layer - Making API request with params:', { 
          view: selectedViewCode,
          admin1Pcode: selectedLocation?.pcode 
        });
        
        // Force a small delay to ensure map is fully initialized
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const response = await axios.get('/api/dtm/map', {
          params: { 
            view: selectedViewCode,
            admin1Pcode: selectedLocation?.pcode
          }
        });

        console.log('DTM Layer - API response:', {
          success: response.data.success,
          dataLength: response.data.data?.length,
          firstItem: response.data.data?.[0],
          hasCoordinates: response.data.data?.[0]?.lat && response.data.data?.[0]?.lon
        });
        
        if (response.data.success && response.data.data && response.data.data.length > 0) {
          const validData = response.data.data.filter(item => 
            item.lat && item.lon && item.idps && Number(item.idps) > 0
          );
          
          console.log(`DTM Layer - Setting ${validData.length} valid data points out of ${response.data.data.length} total`);
          
          if (validData.length === 0) {
            console.warn('DTM Layer - No valid data points with coordinates and IDPs');
          }
          
          setData(validData);
        } else {
          console.warn('DTM Layer - No data returned from API');
          setData([]);
        }
      } catch (error) {
        console.error('Error fetching DTM data:', error);
      }
    };

    fetchData();
  }, [map, selectedViewCode, selectedPillarKey, selectedLocation]);

  // Update circles
  useEffect(() => {
    console.log('DTM Layer - Update circles effect triggered', {
      hasMap: !!map,
      dataLength: data?.length,
      hasCircleGroup: !!circleGroupRef.current,
      selectedPillar: selectedPillarKey
    });
    
    if (!map || !data || !circleGroupRef.current) return;

    try {
      const circleGroup = circleGroupRef.current;

      // Remove old circles
      while (circleGroup.firstChild) {
        circleGroup.removeChild(circleGroup.firstChild);
      }

      // Check if we should display DTM data
      const shouldShowDtm = selectedPillarKey === 'internally_displaced_persons' || 
                          selectedPillarKey === undefined; // Show DTM by default if pillar is undefined
      
      // If pillar is not internally_displaced_persons, don't show any circles
      if (!shouldShowDtm) {
        console.log('DTM Layer - Not showing circles because pillar is not IDP');
        return;
      }
      
      console.log('DTM Layer - Showing circles for IDP data');
      
      console.log('DTM Layer - Starting to render circles for pillar:', selectedPillarKey);

      // Calculate radius scale
      const idpValues = data
        .map(item => Number(item.idps))
        .filter(value => !isNaN(value) && value > 0);
      
      if (idpValues.length === 0) {
        console.warn('DTM Layer - No valid IDP values for scaling');
        return;
      }
      
      const minIdps = Math.min(...idpValues);
      const maxIdps = Math.max(...idpValues);
      console.log('DTM Layer - IDP value range:', { minIdps, maxIdps });
      
      const radiusScale = d3.scaleSqrt()
        .domain([minIdps, maxIdps])
        .range([MIN_RADIUS, MAX_RADIUS]);

      radiusScaleRef.current = radiusScale;

      // Sort data by IDP count in descending order so smaller circles are drawn last
      const sortedData = [...data].sort((a, b) => Number(b.idps) - Number(a.idps));
      
      console.log('DTM Layer - Sorted data for circles:', {
        count: sortedData.length,
        firstFive: sortedData.slice(0, 5).map(d => ({
          admin1_name: d.admin1_name,
          admin1_pcode: d.admin1_pcode,
          idps: d.idps,
          lat: d.lat,
          lon: d.lon
        })),
        allCoordinates: sortedData.map(d => ({ lat: d.lat, lon: d.lon }))
      });

      // Create circles
      console.log('DTM Layer - About to create circles with d3');
      const circles = d3.select(circleGroup)
        .selectAll('circle')
        .data(sortedData)
        .join('circle')
        .attr('class', 'dtm-circle') // Add class for CSS targeting
        .attr('cx', d => {
          const lonLat = fromLonLat([Number(d.lon), Number(d.lat)]);
          const coord = map.getPixelFromCoordinate(lonLat);
          console.log(`DTM Layer - Circle position for ${d.admin1_name}:`, {
            lonLat: [Number(d.lon), Number(d.lat)],
            projectedCoord: lonLat,
            pixelCoord: coord,
            idps: d.idps
          });
          return coord ? coord[0] : 0;
        })
        .attr('cy', d => {
          const coord = map.getPixelFromCoordinate(fromLonLat([Number(d.lon), Number(d.lat)]));
          return coord ? coord[1] : 0;
        })
        .attr('r', d => {
          const radius = radiusScale(Number(d.idps || 0));
          console.log(`DTM Layer - Circle radius for ${d.admin1_name}:`, {
            idps: d.idps,
            radius: radius
          });
          return radius;
        })
        .attr('fill', 'var(--color-secondary)')
        .attr('fill-opacity', 0.15)

        .attr('stroke', 'var(--color-secondary)')
        .attr('stroke-width', '1.5')
        .attr('filter', 'url(#dtm-circle-shadow)')
        .style('pointer-events', 'auto')
        .style('cursor', 'pointer')
        .style('transition', 'fill 0.2s ease')  // Only transition the fill color for hover effects
        .on('mouseover', function(event, d) {
          d3.select(this)
            .attr('fill', 'var(--color-secondary)')
            .attr('stroke-width', '2');
          if (d) showTooltip(event, d);
        })
        .on('mousemove', moveTooltip)
        .on('mouseout', function() {
          d3.select(this)
            .attr('fill', 'var(--color-secondary)')
            .attr('stroke-width', '1.5');
          hideTooltip();
        })
        .on('click', function(event, d) {
          event.stopPropagation();
          d3.select(this)
            .attr('fill', 'var(--color-secondary)')
            .attr('fill-opacity', 0.5)
            .attr('stroke-width', '2.5');
          
          // Reset all other circles
          d3.selectAll('circle').filter(function() { return this !== event.target; })
            .attr('fill', 'var(--color-secondary)')
            .attr('stroke-width', '1.5');
            
          if (onLocationSelect && d.admin1_pcode && d.admin1_name) {
            onLocationSelect({
              adm1_name: d.admin1_name,
              name: d.admin1_name,
              pcode: d.admin1_pcode,
              lat: d.lat,
              lon: d.lon
            });
          }
        });

      // Function to update circle positions
      const updateCirclePositions = () => {
        console.log('DTM Layer - Updating circle positions');
        circles
          .attr('cx', d => {
            const coord = map.getPixelFromCoordinate(fromLonLat([Number(d.lon), Number(d.lat)]));
            return coord ? coord[0] : 0;
          })
          .attr('cy', d => {
            const coord = map.getPixelFromCoordinate(fromLonLat([Number(d.lon), Number(d.lat)]));
            return coord ? coord[1] : 0;
          });
          
        // Log circle positions after update
        console.log('DTM Layer - Circle positions after update:', {
          circleCount: circles.size(),
          firstCircle: circles.nodes()[0] ? {
            cx: circles.nodes()[0].getAttribute('cx'),
            cy: circles.nodes()[0].getAttribute('cy'),
            r: circles.nodes()[0].getAttribute('r')
          } : 'No circles found',
          containerVisible: containerRef.current?.style.display,
          containerZIndex: containerRef.current?.style.zIndex
        });
      };

      // Add map movement and resize listeners
      const moveHandler = () => {
        requestAnimationFrame(updateCirclePositions);
      };

      // Resize handler
      const handleResize = () => {
        updateCirclePositions();
      };

      // Named handlers for movestart/moveend so they can be properly removed
      const moveStartHandler = () => {
        updateCirclePositions();
      };

      const moveEndHandler = () => {
        updateCirclePositions();
      };

      map.on('movestart', moveStartHandler);
      map.on('moveend', moveEndHandler);

      // Add listeners for continuous updates during movement
      map.on(['pointerdrag', 'postrender'], moveHandler);
      
      // Add window resize listener
      window.addEventListener('resize', handleResize);

      return () => {
        map.un(['pointerdrag', 'postrender'], moveHandler);
        map.un('movestart', moveStartHandler);
        map.un('moveend', moveEndHandler);
        window.removeEventListener('resize', handleResize);
      };
    } catch (error) {
      console.error('Error updating DTM circles:', error);
    }
  }, [map, data, selectedPillarKey]);

  // Show tooltip
  const showTooltip = (event, item) => {
    const tooltip = tooltipRef.current;
    if (!tooltip) return;

    // Hide any Admin1Layer tooltips
    const admin1Tooltips = document.querySelectorAll('.ol-tooltip');
    admin1Tooltips.forEach(tip => {
      tip.style.display = 'none';
    });

    const tooltipContent = `
      <div class="dtm-tooltip-content">
        <div class="dtm-tooltip-header">
          <span class="dtm-tooltip-badge">${item.adm1_name || 'Unknown'}</span>
          <span class="dtm-tooltip-badge">${item.admin1_pcode || 'No PCode'}</span>
        </div>
        <div class="dtm-tooltip-body">
          <div><span class="dtm-label">Date:</span> <span>${formatDate(item.reporting_date)}</span></div>
          <div><span class="dtm-label">Round:</span> <span>${item.round_number || 'N/A'}</span></div>
          <div><span class="dtm-label">IDPs:</span> <span class="dtm-value-highlight">${Number(item.idps).toLocaleString()}</span></div>
        </div>
      </div>
    `;

    tooltip.innerHTML = tooltipContent;
    tooltip.style.display = 'block';
    tooltip.style.left = `${event.clientX + 10}px`;
    tooltip.style.top = `${event.clientY + 10}px`;
  };

  // Move tooltip
  const moveTooltip = (event) => {
    const tooltip = tooltipRef.current;
    if (tooltip) {
      tooltip.style.left = `${event.clientX + 10}px`;
      tooltip.style.top = `${event.clientY + 10}px`;
    }
  };

  // Hide tooltip
  const hideTooltip = () => {
    const tooltip = tooltipRef.current;
    if (tooltip) {
      tooltip.style.display = 'none';
    }
  };

  // Update container visibility and z-index
  useEffect(() => {
    // Check if we should display DTM data
    const shouldShowDtm = selectedPillarKey === 'internally_displaced_persons';
    
    console.log('DTM Layer - Updating container visibility:', {
      hasContainer: !!containerRef.current,
      selectedPillarKey,
      shouldBeVisible: shouldShowDtm,
      defaultPillar: selectedPillarKey === undefined ? 'using default (internally_displaced_persons)' : 'using selected pillar'
    });
    
    if (containerRef.current) {
      // Force visibility when pillar is internally_displaced_persons
      if (shouldShowDtm) {
        console.log('DTM Layer - Forcing visibility for IDP pillar');
        containerRef.current.style.display = 'block';
        containerRef.current.style.visibility = 'visible';
        containerRef.current.style.opacity = '1';
      } else {
        containerRef.current.style.display = 'none';
      }
      
      // Ensure the container is above the map
      if (shouldShowDtm) {
        containerRef.current.style.zIndex = '9999';
        containerRef.current.style.position = 'absolute';
        containerRef.current.style.pointerEvents = 'auto';
        
        const svg = containerRef.current.querySelector('svg');
        if (svg) {
          svg.style.zIndex = '9999';
          svg.style.position = 'absolute';
          svg.style.visibility = 'visible';
          svg.style.opacity = '1';
        }
        
        // Make sure circles are visible
        const circles = containerRef.current.querySelectorAll('circle');
        circles.forEach(circle => {
          circle.style.pointerEvents = 'auto';
          circle.style.visibility = 'visible';
          circle.style.opacity = '1';
        });
        
        console.log('DTM Layer - Set container to visible with very high z-index');
      }
    }
  }, [selectedPillarKey]);

  return null;
};

export default DtmLayer;
